import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogIn, LogOut, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';
import { navItems, type NavItem, type RouteAccess } from '@/nav-items';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useSidebar } from '@/components/Navigation/SidebarContext';

/**
 * Routes that should never appear in the sidebar.
 * Auth utilities go here; profile gets its own spot in the user area.
 */
const HIDDEN_ROUTES = new Set(['/auth', '/auth/callback', '/auth/reset', '/profile']);

/**
 * Category definitions that map route paths to groups.
 * Order within each category matches the desired display order.
 */
interface NavCategory {
  label: string;
  paths: string[];
  /** Only show this category to users with this access level */
  requiredAccess?: RouteAccess;
  /** Start collapsed? */
  defaultCollapsed?: boolean;
  /** Category icon shown in collapsed sidebar mode */
  icon?: React.ComponentType<{ className?: string }>;
}

const CATEGORIES: NavCategory[] = [
  {
    label: 'Core',
    paths: ['/', '/canvas', '/cloud-foundation', '/dependency-planning', '/documentation', '/docs'],
  },
  {
    label: 'Tools',
    paths: [
      '/idp-templates', // Recovery P0 priority: IDP artifacts visualizer (Canvas ?template= + risk)
      '/idp-tools-catalog', // Recovery: live cyber/OSINT risk badges + Live Risk
      '/testing-dashboard',
      '/advanced-features',
      '/self-healing',
      '/marketplace',
      '/vclusters',
      '/validation',
    ],
  },
  {
    label: 'Admin',
    paths: ['/enterprise-management', '/admin/metrics', '/admin/sso'],
    requiredAccess: 'admin',
  },
];

/**
 * Extracts the icon component from a NavItem's JSX element.
 */
function extractIcon(item: NavItem): React.ComponentType<{ className?: string }> {
  const iconElement = item.icon as React.ReactElement;
  return iconElement.type as React.ComponentType<{ className?: string }>;
}

interface SidebarNavItemProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
}

function SidebarNavItem({ item, active, collapsed }: SidebarNavItemProps) {
  const Icon = extractIcon(item);

  const link = (
    <Link
      to={item.to}
      className={cn(
        'flex items-center gap-3 rounded-lg min-h-[44px] px-3 py-2',
        'transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        active ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-accent',
        collapsed && 'justify-center px-2'
      )}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.title : undefined}
    >
      <Icon
        className={cn('h-4 w-4 flex-shrink-0', active ? 'text-primary' : 'text-muted-foreground')}
        aria-hidden="true"
      />
      {!collapsed && <span className="text-sm truncate">{item.title}</span>}
      {!collapsed && item.access === 'admin' && (
        <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
          Admin
        </span>
      )}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

interface SidebarCategoryProps {
  label: string;
  items: NavItem[];
  collapsed: boolean;
  defaultOpen?: boolean;
  categoryIcon?: React.ComponentType<{ className?: string }>;
  isActive: (path: string) => boolean;
}

function SidebarCategory({
  label,
  items,
  collapsed,
  defaultOpen = true,
  categoryIcon: CategoryIcon,
  isActive,
}: SidebarCategoryProps) {
  const [open, setOpen] = useState(defaultOpen);

  const hasActiveItem = items.some(item => isActive(item.to));

  // Auto-expand when a child route is active
  React.useEffect(() => {
    if (hasActiveItem && !open) {
      setOpen(true);
    }
  }, [hasActiveItem]);

  if (items.length === 0) return null;

  if (collapsed) {
    return (
      <div className="flex flex-col gap-0.5">
        {items.map(item => (
          <SidebarNavItem
            key={item.to}
            item={item}
            active={isActive(item.to)}
            collapsed={collapsed}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={() => setOpen(prev => !prev)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider',
          'text-muted-foreground hover:text-foreground transition-colors duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded'
        )}
        aria-expanded={open}
      >
        {CategoryIcon && <CategoryIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />}
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          className={cn('h-3 w-3 transition-transform duration-200', !open && '-rotate-90')}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 mt-0.5">
          {items.map(item => (
            <SidebarNavItem
              key={item.to}
              item={item}
              active={isActive(item.to)}
              collapsed={collapsed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface DesktopSidebarProps {
  className?: string;
}

export function DesktopSidebar({ className }: DesktopSidebarProps) {
  const location = useLocation();
  const { session, user, signOut } = useAuth();
  const { isSuperUser } = useUserRole();
  const { collapsed, toggle } = useSidebar();

  // Modern best practice: persist collapsed state (localStorage) for desktop power users
  // Aligns with recovery impact: fast access to IDP Templates / cyber catalog without re-config every session
  // Uses useEffect for side effect only; context owns state
  React.useEffect(() => {
    try {
      localStorage.setItem('desktop-sidebar-collapsed', String(collapsed));
    } catch {
      // ignore storage errors (private mode etc)
    }
  }, [collapsed]);

  const isAuthenticated = !!session;
  const isAdmin = isSuperUser; // Real role from DB via useUserRole (fixes P2: non-super_user must not see admin nav)
  // HEALTH: see src/hooks/useUserRole.ts note on direct client db.select (CSP/401/Drizzle browser issues). Keep usage minimal.

  const hasAccess = (access: RouteAccess): boolean => {
    switch (access) {
      case 'public':
      case 'trial':
        return true;
      case 'authenticated':
        return isAuthenticated;
      case 'admin':
        return isAdmin;
      default:
        return false;
    }
  };

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  /** Build the nav items lookup once per render */
  const navItemsByPath = useMemo(() => {
    const map = new Map<string, NavItem>();
    for (const item of navItems) {
      map.set(item.to, item);
    }
    return map;
  }, []);

  /** Compute categorized items, filtering by access and hidden routes */
  const categorizedItems = useMemo(() => {
    return CATEGORIES.map(category => {
      if (category.requiredAccess && !hasAccess(category.requiredAccess)) {
        return { ...category, items: [] };
      }

      const items = category.paths
        .filter(path => !HIDDEN_ROUTES.has(path))
        .map(path => navItemsByPath.get(path))
        .filter((item): item is NavItem => !!item && hasAccess(item.access));

      return { ...category, items };
    });
  }, [isAuthenticated, isAdmin, navItemsByPath]);

  const avatarInitial = user?.email?.charAt(0).toUpperCase() || '?';
  const displayName = user?.user_metadata?.full_name || user?.email || 'User';

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col fixed left-0 top-0 bottom-0 z-40',
        'border-r border-border bg-background',
        'transition-all duration-200',
        collapsed ? 'w-16' : 'w-60',
        className
      )}
      aria-label="Desktop navigation"
      role="navigation"
    >
      {/* Logo / Brand area */}
      <div
        className={cn(
          'flex items-center h-14 border-b border-border px-3 flex-shrink-0',
          collapsed ? 'justify-center' : 'gap-2'
        )}
      >
        <Link
          to="/"
          className={cn(
            'flex items-center gap-2 rounded-lg p-1',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
          aria-label="Platform Home"
        >
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-bold text-sm">PE</span>
          </div>
          {!collapsed && (
            <span className="text-sm font-semibold text-foreground truncate">Platform Nexus</span>
          )}
        </Link>
      </div>

      {/* Scrollable nav area */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4" aria-label="Main navigation">
        {categorizedItems.map(category => (
          <SidebarCategory
            key={category.label}
            label={category.label}
            items={category.items}
            collapsed={collapsed}
            defaultOpen={!category.defaultCollapsed}
            categoryIcon={category.icon}
            isActive={isActive}
          />
        ))}
      </nav>

      {/* User area + collapse toggle */}
      <div className="flex-shrink-0 border-t border-border p-2 space-y-1">
        {/* User section */}
        {isAuthenticated ? (
          <>
            <Link
              to="/profile"
              className={cn(
                'flex items-center gap-3 rounded-lg min-h-[44px] px-3 py-2',
                'transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive('/profile')
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground hover:bg-accent',
                collapsed && 'justify-center px-2'
              )}
              aria-label="Your profile"
            >
              {user?.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt=""
                  className="h-6 w-6 rounded-full flex-shrink-0"
                />
              ) : (
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-primary">{avatarInitial}</span>
                </div>
              )}
              {!collapsed && <span className="text-sm truncate">{displayName}</span>}
            </Link>
            <button
              onClick={() => signOut()}
              className={cn(
                'flex items-center gap-3 rounded-lg min-h-[44px] px-3 py-2 w-full',
                'text-muted-foreground hover:text-foreground hover:bg-accent',
                'transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                collapsed && 'justify-center px-2'
              )}
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {!collapsed && <span className="text-sm">Sign Out</span>}
            </button>
          </>
        ) : (
          <Link
            to="/auth"
            className={cn(
              'flex items-center gap-3 rounded-lg min-h-[44px] px-3 py-2',
              'text-foreground hover:bg-accent',
              'transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              collapsed && 'justify-center px-2'
            )}
            aria-label="Sign in to your account"
          >
            <LogIn className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            {!collapsed && <span className="text-sm">Sign In</span>}
          </Link>
        )}

        {/* Collapse toggle */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              onClick={toggle}
              className={cn(
                'flex items-center gap-3 rounded-lg min-h-[44px] px-3 py-2 w-full',
                'text-muted-foreground hover:text-foreground hover:bg-accent',
                'transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                collapsed && 'justify-center px-2'
              )}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              ) : (
                <>
                  <PanelLeftClose className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span className="text-sm">Collapse</span>
                </>
              )}
            </button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" sideOffset={8}>
              Expand sidebar
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </aside>
  );
}

export default DesktopSidebar;
